/**
 * "Is this the page I am on?" for navigation highlighting.
 *
 * Shared by the desktop nav and the mobile menu so the two cannot disagree about
 * which item is current.
 */

/**
 * Percent-decoded, trailing slash removed; the site root stays `/`.
 *
 * Decoding is what makes this work in Thai at all. Category and page hrefs are
 * written as real UTF-8 (`/th/product-category/เมล็ดพันธุ์เพศเมีย`), but
 * `page.url.pathname` comes from a `URL`, which percent-encodes every non-ASCII
 * byte. Comparing the two raw forms never matches, so a Thai visitor would see
 * no current-page highlight anywhere — and it would look fine in English, which
 * is exactly how this sort of thing ships.
 */
function normalise(path: string): string {
	let decoded = path;
	try {
		decoded = decodeURIComponent(path);
	} catch {
		// Malformed escape sequence: compare what we were given rather than
		// throwing in the middle of rendering the header.
	}
	return decoded.replace(/\/+$/, '') || '/';
}

/**
 * True when `path` is `href` or sits beneath it.
 *
 * The descendant test appends a slash before comparing, and that is not
 * cosmetic: a bare `startsWith('/th')` once matched
 * `/the-importance-of-genetics-…` and filed a published English post under Thai.
 * `'/learn/page/2'.startsWith('/learn/')` is a section match;
 * `'/learning-to-grow'` is not.
 *
 * The site root is compared exactly, since every path is "beneath" it.
 */
export function isActivePath(path: string, href: string | undefined): boolean {
	if (!href) return false;

	const current = normalise(path);
	const target = normalise(href);

	if (target === '/') return current === '/';
	return current === target || current.startsWith(`${target}/`);
}

/**
 * True when the current path is any product listing — a category archive or an
 * individual strain page.
 *
 * Used to mark the "Cannabis Seeds" parent as current while the visitor is
 * anywhere in the catalogue, which no single `href` comparison can express
 * because the dropdown's own link points at one category among many.
 */
export function isCatalogPath(path: string): boolean {
	const current = normalise(path);
	// These segments are never translated, so the same list serves both locales.
	return ['/product-category', '/cannabis-seeds', '/shop'].some(
		(section) =>
			current === section ||
			current.startsWith(`${section}/`) ||
			// Thai catalogue URLs carry the `/th` prefix; the segments after it are
			// the same, because only page slugs are translated, not these.
			current === `/th${section}` ||
			current.startsWith(`/th${section}/`)
	);
}
